import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import store from "store2";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useQuery } from "react-query";
import { format, subDays } from "date-fns";
import { isEmpty, find } from "lodash";
import { formatInTimeZone } from "date-fns-tz";
import { v4 as uuid } from "uuid";
import NavigationBookingButton from "@/kyna/ui/button/NavigationBookingButton";
import KNDatePicker from "@/kyna/ui/datepicker";
import KNToaster from "@/kyna/ui/toaster/KNToaster";
import { KNThemeError, KNThemeLoading } from "@/kyna/ui/toaster/KNTheme";
import InputForm from "@/kyna/forms/InputForm";
import { KTGet, KTPost } from "@/kyna/lib/db";
import { PublicBookingSchema } from "@/kyna/lib/validations/PublicBookingSchema";
import SlotFullModal from "./modules/SlotFullModal";
import KNModal from "@/kyna/ui/modal/KNModal";
import { Widget } from "@uploadcare/react-widget";
import { deleteImage } from "@/kyna/lib/DeleteAPIUploadCare";

const BookingForm = () => {
  const transaction_no = store.get("transaction_no");
  const [availableTimeSlot, setAvailableTimeSlot] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [scheduleID, setScheduleID] = useState([]);
  const [dateIsLoading, setDateIsLoading] = useState(false);
  const [isTimeSlotLoading, setTimeSlotLoading] = useState(false);
  const [oneTimeFetch, setOneTimeFetch] = useState(true);
  const [isSlotFullModalOpen, setIsSlotFullModalOpen] = useState(false);
  const [hasUploadedReferralLetter,setHasUploadedReferralLetter] = useState(false);
  const timeRef = useRef();
  const router = useRouter();
  const { data, isLoading, status } = useQuery(
    ["booking"],
    () =>
      transaction_no &&
      KTGet("/api/booking/public", {
        where: `transaction_no:${transaction_no}`,
      }),
    {
      enabled: !isEmpty(transaction_no),
    }
  );
  const formik = useFormik({
    initialValues: {
      bookingFor: "",
      relationshipWithThePatient: "",
      concerns: "",
      date: "",
      service_id: "",
      schedule_id: "",
      time: "",
      informant_name: "",
      referral_letter: "",
    },
    validationSchema: PublicBookingSchema,
    onSubmit: async (values) => {
      toast.loading("Submitting...", {
        id: "submit-toast",
        ...KNThemeLoading,
      });
      let response = {};
      let transactionNo = transaction_no ? transaction_no : uuid();
      if (transaction_no) {
        response = await KTPost(
          `api/booking/public?transaction_no=${transaction_no}`,
          {
            bookingFor: values.bookingFor,
            relationshipWithThePatient: values.relationshipWithThePatient,
            informant_name: values.informant_name,
            concerns: values.concerns,
            schedule_id: parseInt(values.time),
            service_id: parseInt(values.service_id),
          }
        );
      } else {
        response = await KTPost("api/booking/public", {
          service_id: parseInt(values.service_id),
          schedule_id: parseInt(values.time),
          transaction_no: transactionNo,
          bookingFor: values.bookingFor,
          relationshipWithThePatient: values.relationshipWithThePatient,
          concerns: values.concerns,
          informant_name: values.informant_name,
        });
      }
      console.log(response?.data);
      if (response?.data?.success) {
        store.set("transaction_no", transactionNo);
        //TODO : delete images when they are going back to the tab the
        //the images will upload always when they back to booking tab then click the next arrow
        if (values.referral_letter && hasUploadedReferralLetter) {
          const mediaData = {
            path: values.referral_letter.cdnUrl,
            file_name: values.referral_letter.name,
            orig_name: values.referral_letter.name,
            attach_id: 0, // users uploaded the image,
            attachment_for: "Booking - Referral Letter",
            user_id: 0,
            booking_id: response?.data?.booking?.id, // booking id,
            is_image: values.referral_letter.isImage,
            reference_id: values.referral_letter.uuid,
          };
          const mediaResponse = await KTPost("/api/media/applicant", mediaData);
          if (mediaResponse?.data?.success) {
            toast.dismiss("submit-toast");
            router.push("/booking/patient");
          } else {
            toast.error("Error, Cannot save the referral letter", {
              ...KNThemeError,
            });
            toast.dismiss("submit-toast");
            return;
          }
        }
        toast.dismiss("submit-toast");
        router.push("/booking/patient");
      } else if (
        response?.error?.response?.status == 404 &&
        response?.error?.response?.data?.err == "Slot is full"
      ) {
        toast.dismiss("submit-toast");
        setIsSlotFullModalOpen(true);
      } else {
        toast.dismiss("submit-toast");
        toast.error("Error Occured, Please try again", {
          ...KNThemeError,
        });
      }
    },
  });
  const {
    data: dateData,
    isLoading: slotIsLoading,
    refetch: dateRefetch,
    status: slotStatus,
  } = useQuery(
    ["Slot", formik.values.service_id],
    () =>
      formik.values.service_id &&
      KTGet("api/doctors/", {
        where: `service_id:${formik.values.service_id}`,
      }),
    { enabled: formik.values.service_id ? true : false }
  );
  const getServiceCategory = async (category) => {
    return await KTGet("api/maintenance/_service", {
      orderBy: "modified,asc",
      category: category,
    });
  };
  const { data: AdultData } = useQuery("AdultData", () =>
    getServiceCategory("Adult")
  );
  const { data: childData } = useQuery("childData", () =>
    getServiceCategory("Child and Adolescent")
  );

  const slotFull = () => {
    setIsSlotFullModalOpen(false);
  };

  const adult = AdultData?.data?.result;
  const child = childData?.data?.result;

  useEffect(() => {
    formik.setValues({
      bookingFor: data?.data?.result?.bookingFor
        ? data?.data?.result?.bookingFor
        : "",
      relationshipWithThePatient: data?.data?.result?.relationshipWithThePatient
        ? data?.data?.result?.relationshipWithThePatient
        : "",
      informant_name: data?.data?.result?.informant_name
        ? data?.data?.result?.informant_name
        : "",
      service_id: data?.data?.result?.service_id
        ? data?.data?.result?.service_id
        : "",
      concerns: data?.data?.result?.concerns
        ? data?.data?.result?.concerns
        : "",
      date: data?.data?.result?.date
        ? new Date(data?.data?.result?.schedule.schedule_date)
        : "",
      time: data?.data?.result?.schedule?.id
        ? data?.data?.result?.schedule?.id
        : "",
      referral_letter: find(data?.data?.result, {attachment_for: "Booking - Referral Letter"})
    });
  }, [data?.data?.result]);

  useEffect(() => {
    const dates = dateData?.data?.result?.filter((i) => {
      return (
        new Date(i.schedule_date) >
          new Date(format(new Date(), "MM dd yyyy")) &&
        i.occupied_slot !== i.max_slot &&
        i.occupied_slot < i.max_slot
      );
    });
    setAvailableDates(
      dates?.map((i) => {
        return formatInTimeZone(i.schedule_date, "Asia/Taipei", "yyyy-MM-dd");
      })
    );
    formik.setFieldValue(
      "date",
      data?.data?.result?.schedule?.schedule_date
        ? oneTimeFetch
          ? new Date(data?.data?.result?.schedule?.schedule_date)
          : ""
        : ""
    );
    setDateIsLoading(false);
  }, [dateData?.data?.result]);
  useEffect(async () => {
    setTimeSlotLoading(true);
    const response =
      formik.values.service_id &&
      formik.values.date &&
      (await KTPost("/api/doctors/check_slot", {
        service_id: formik.values.service_id,
        schedule_date:
          format(
            new Date(subDays(new Date(formik.values.date), 1)),
            "yyyy-MM-dd"
          ) + "T16:00:00.000Z",
      }));
    let timeSlot = [];
    let id = [];
    response?.data?.result?.map((i) => {
      if (
        timeSlot.includes(
          `${format(new Date(i.start_time), "hh:mm a")} - ${format(
            new Date(i.end_time),
            "hh:mm a"
          )}`
        )
      ) {
        return;
      } else {
        id.push(i.id);
        timeSlot.push(
          `${format(new Date(i.start_time), "hh:mm a")} - ${format(
            new Date(i.end_time),
            "hh:mm a"
          )}`
        );
      }
    });
    setAvailableTimeSlot(timeSlot);
    setTimeSlotLoading(false);
    setScheduleID(id);
    formik.setFieldValue(
      "time",
      data?.data?.result?.schedule?.id
        ? oneTimeFetch
          ? data?.data?.result?.schedule?.id
          : ""
        : ""
    );
  }, [formik.values.date]);
  useEffect(() => {
    setDateIsLoading(true);
    dateRefetch();
    !isEmpty(availableTimeSlot) && setOneTimeFetch(false);
    timeRef.current.selected = "Select Date First";
  }, [formik.values.service_id]);

  return (
    <>
      <div className="tab-content-wrap">
        <p className="text-blue text-lg font-bold">I am booking</p>
        <div className="mb-4"></div>
        <form onSubmit={formik.handleSubmit} autoComplete={"off"}>
          <div className="flex gap-x-5">
            <div className="flex items-center gap-x-2">
              <input
                className="h-4 w-4 transition duration-200 focus:outline-none"
                type="radio"
                name="radio group"
                id="for myself"
                value="myself"
                checked={formik.values.bookingFor === "myself"}
                onChange={(e) => {
                  formik.setFieldValue("bookingFor", e.target.value);
                }}
              />
              <label
                className="form-check-label inline-block text-gray-800"
                htmlFor="for myself"
              >
                For myself
              </label>
            </div>
            <div className="flex items-center gap-x-2">
              <input
                className="h-4 w-4 transition duration-200 focus:outline-none"
                type="radio"
                name="radio group"
                id="for others"
                checked={formik.values.bookingFor === "others"}
                value="others"
                onChange={(e) => {
                  formik.setFieldValue("bookingFor", e.target.value);
                }}
              />
              <label
                className="form-check-label inline-block text-gray-800"
                htmlFor="for others"
              >
                For others
              </label>
            </div>
          </div>
          <div className="help-block text-red-400 text-xs mt-2">
            {formik.touched.bookingFor && formik.errors.bookingFor && (
              <div>{formik.errors.bookingFor}</div>
            )}
          </div>
          <div className="mb-8" />
          {formik.values.bookingFor === "others" ? (
            <div className="my-5">
              <label className="text-blue text-lg font-semibold">
                Relation with the Patient
              </label>
              <select
                name="sched_time"
                id="sched_time"
                value={formik.values.relationshipWithThePatient}
                onChange={(e) => {
                  formik.setFieldValue(
                    "relationshipWithThePatient",
                    e.target.value
                  );
                }}
                className="w-full mt-3 outline-none form-control"
              >
                <option value="">Select</option>
                <option value="Immediate Family">Immediate Family</option>
                <option value="Friend">Friend</option>
                <option value="Colleague">Colleague</option>
              </select>
              <div className="help-block text-red-400 text-xs mt-2">
                {formik.touched.relationshipWithThePatient &&
                  formik.errors.relationshipWithThePatient && (
                    <div>{formik.errors.relationshipWithThePatient}</div>
                  )}
              </div>
              <InputForm
                type="text"
                label="Informant Name"
                labelClassName="control-label text-blue text-lg font-semibold"
                id="informant_name"
                name="informant_name"
                formik={formik}
                withFormik={true}
                placeholder="John Michael Doe"
              />
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-5">
            <div className="col-span-2">
              <label
                htmlFor="service"
                className="font-semibold text-blue text-lg block"
              >
                Service
              </label>

              <div>
                <div className="text-base lg:text-sm xl:text-base font-medium mr-0 text-black block lg:mb-0 lg:inline-block lg:mt-0 w-full">
                  <div className="relative pt-1 group rounded-md w-full">
                    <select
                      name="service"
                      id="service"
                      value={formik.values.service_id}
                      onChange={(e) => {
                        formik.setFieldValue("service_id", e.target.value);
                      }}
                      className="outline-none w-full form-control"
                    >
                      <option value="">Select Service</option>
                      <optgroup readOnly label="Adult">
                        {adult?.map((i, key) => {
                          return (
                            <option
                              title={i.description}
                              value={i.id}
                              key={key}
                            >
                              {i.name}
                            </option>
                          );
                        })}
                      </optgroup>
                      <optgroup label="Child and Adolescents">
                        {child?.map((i, key) => {
                          return (
                            <option
                              title={i.description}
                              value={i.id}
                              key={key}
                            >
                              {i.name}
                            </option>
                          );
                        })}
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div className="help-block text-red-400 text-xs mt-2">
                  {formik.touched.service_id && formik.errors.service_id && (
                    <div>{formik.errors.service_id}</div>
                  )}
                </div>
              </div>
            </div>
            {formik.values.service_id == 4 || formik.values.service_id == 9 ? (
              <div className="col-span-2 space-y-1">
                <label
                  htmlFor="service"
                  className="font-semibold text-blue text-lg block"
                >
                  Referral Letter*
                </label>
                {formik.values.referral_letter ? (
                  <a className="block mb-1 underline underline-offset-5" target={"_blank"} rel="noreferrer" href={formik.values.referral_letter.path}>{formik.values.referral_letter.file_name}</a>
                ):null}
                <Widget
                  className=""
                  publicKey={process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY}
                  id="referral_letter"
                  name="referral_letter"
                  tabs="file"
                  onFileSelect={(file) => {
                    if (file) {
                      !isEmpty(formik.values.referral_letter) &&
                        console.log(
                          deleteImage(formik.values.referral_letter?.uuid)
                        );
                      file.progress((info) =>
                        console.log("File progress: ", info.progress)
                      );
                      file.done((info) => {
                        console.log("File uploaded: ", info);
                        // setCVPic(info.cdnUrl)
                        setHasUploadedReferralLetter(true);
                        formik.setFieldValue("referral_letter", info);
                        // saveMediaFile(info)
                      });
                    }
                  }}
                />
                <div className="help-block text-red-400 text-xs mt-2">
                  {formik.errors.referral_letter && (
                    <div>{formik.errors.referral_letter}</div>
                  )}
                </div>
              </div>
            ) : null}
            <div className="col-span-2 md:col-span-1 booking-date">
              <label className="text-blue text-lg font-semibold">Date</label>
              <KNDatePicker
                key={formik.values.service_id}
                setDate={(date) => {
                  formik.setFieldValue("date", date);
                }}
                disabled={formik.values.service_id ? false : true}
                startDate={
                  status === "success" &&
                  data?.data?.result?.schedule?.schedule_date &&
                  oneTimeFetch
                    ? formik.values.date
                    : null
                }
                placeholderText={
                  formik.values.service_id
                    ? !isEmpty(availableDates)
                      ? "Select Date"
                      : dateIsLoading
                      ? "Loading ..."
                      : "No Available Slot"
                    : "Select Service First"
                }
                includeDates={availableDates}
              />
              <div className="help-block text-red-400 text-xs mt-2">
                {formik.touched.date && formik.errors.date && (
                  <div>{formik.errors.date}</div>
                )}
              </div>
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="font-semibold text-lg text-blue">Time</label>
              <select
                name="sched_time"
                id="sched_time"
                ref={timeRef}
                value={formik.values.time}
                onChange={(e) => {
                  formik.setFieldValue("time", e.target.value);
                }}
                className="w-full shadow-none outline-none form-control"
              >
                <option value="">
                  {isTimeSlotLoading
                    ? "Loading..."
                    : !formik.values.service_id
                    ? "Select Service First"
                    : !formik.values.date
                    ? "Select Date First"
                    : availableTimeSlot.length > 0
                    ? "Select Time"
                    : "No available time slot"}
                </option>
                {availableTimeSlot?.map((i, key) => {
                  return (
                    <option key={key} value={scheduleID[key]}>
                      {i}
                    </option>
                  );
                })}
              </select>
              <div className="help-block text-red-400 text-xs mt-2">
                {formik.touched.time && formik.errors.time && (
                  <div>{formik.errors.time}</div>
                )}
              </div>
            </div>
            <div className="col-span-2">
              <label className="form-label inline-block mb-2 text-lg font-semibold text-blue">
                Concerns
              </label>
              <textarea
                className="form-control h-28"
                id="concerns"
                rows="3"
                value={formik.values.concerns}
                onChange={(e) => {
                  formik.setFieldValue("concerns", e.target.value);
                }}
                placeholder="Please note here your concerns"
              />
              <div className="help-block text-red-400 text-xs mt-2">
                {formik.touched.concerns && formik.errors.concerns && (
                <div>{formik.errors.concerns}</div>
                )}
            </div>
            </div>
          </div>

          <div className="grid grid-cols-1 mt-10">
            <div className="col-span-1 justify-self-end">
              <NavigationBookingButton type={"submit"} />
            </div>
          </div>
        </form>
      </div>
      <KNModal
        isOpen={isSlotFullModalOpen}
        noFooter={true}
        bodyClass={"max-w-4xl mt-10"}
        closeModal={slotFull}
        body={<SlotFullModal slotFull={slotFull} />}
      />
      <KNToaster />
    </>
  );
};

export default BookingForm;
